import React, {useMemo} from 'react'
import {Offer, ServiceOffer} from '../../types'
import clsx from 'clsx'
import {formatCurrency} from '../../services/utils'
import {formatDisplayDate, toPropertyDateTime} from '../../services/dateUtils'
import {calcOfferPriceSummary, isPrepaidOffer} from '../../services/offerUtils'
import {getLogger} from '../../services/logging'
import {pushDataLayerEvent} from '../../services/dataLayer'

const logger = getLogger('ShoppingCartPricingSummary')

export interface ShoppingCartPricingSummaryProps {
  offer: Offer
  additionalServices: ServiceOffer[]
  className?: string
}

const ShoppingCartPricingSummary = ({
  offer,
  additionalServices,
  className
}: ShoppingCartPricingSummaryProps) => {
  const [showItemizedRoomCharges, setShowItemizedRoomCharges] = React.useState(
    false
  )
  const [
    showItemizedIncludedServices,
    setShowItemizedIncludedServices
  ] = React.useState(false)
  const [
    showItemizedAdditionalServices,
    setShowItemizedAdditionalServices
  ] = React.useState(false)
  const [
    showItemizedTaxesAndFees,
    setShowItemizedTaxesAndFees
  ] = React.useState(false)

  const pricingSummary = useMemo(() => {
    return calcOfferPriceSummary(offer, additionalServices)
  }, [offer, additionalServices])
  const {
    itemizedRoomCharges,
    totalRoomCharges,
    itemizedIncludedServices,
    totalIncludedServices,
    itemizedAdditionalServices,
    totalAdditionalServices,
    itemizedTaxesAndFees,
    totalTaxesAndFees,
    total
  } = pricingSummary

  const isPrepaid: boolean = useMemo(() => {
    return isPrepaidOffer(offer)
  }, [offer])

  logger.debug({
    message: 'Rendering shopping cart pricing summary...',
    data: {
      pricingSummary,
      offer,
      additionalServices
    }
  })

  const handleToggleRoomCharges = () => {
    setShowItemizedRoomCharges(!showItemizedRoomCharges)

    pushDataLayerEvent({
      event: 'Shopping_Cart_Toggle_Room_Charges',
      category: 'IBE',
      value: 1
    })
  }

  const handleToggleIncludedServices = () => {
    setShowItemizedIncludedServices(!showItemizedIncludedServices)
    pushDataLayerEvent({
      event: 'Shopping_Cart_Toggle_Included_Service_Charges',
      category: 'IBE',
      value: 1
    })
  }

  const handleToggleAdditionalServices = () => {
    setShowItemizedAdditionalServices(!showItemizedAdditionalServices)

    pushDataLayerEvent({
      event: 'Shopping_Cart_Toggle_Addon_Service_Charges',
      category: 'IBE',
      value: 1
    })
  }

  return (
    <div className={clsx('w-full flex flex-col items-end gap-0.5', className)}>
      <table className="price-details text-light-primary">
        <tbody>
          {/* room charges */}
          {totalRoomCharges > 0 && (
            <>
              <tr className="font-medium">
                <td>
                  <span className="mr-2">Total Room Charges</span>
                  <a
                    className="text-xs link text-accent no-underline hover:underline"
                    onClick={() => handleToggleRoomCharges()}
                    onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                      if (e.key === 'Enter') {
                        handleToggleRoomCharges()
                      }
                    }}
                    tabIndex={0}
                    aria-label="Toggle itemized room charges."
                  >
                    {showItemizedRoomCharges ? 'hide' : 'show'}
                  </a>
                </td>
                <td className="price">{formatCurrency(totalRoomCharges)}</td>
              </tr>
              {showItemizedRoomCharges &&
                itemizedRoomCharges.map(({name, date, amount}) => {
                  return (
                    <tr key={`${name}-${date}`} className="text-xs">
                      <td>
                        {name} - {formatDisplayDate(toPropertyDateTime(date))}
                      </td>
                      <td className="price">{formatCurrency(amount)}</td>
                    </tr>
                  )
                })}
            </>
          )}

          {/* included services */}
          {totalIncludedServices > 0 && (
            <>
              <tr className="font-medium">
                <td>
                  <span className="mr-2">Total Included Services</span>
                  <a
                    className="text-xs link text-accent no-underline hover:underline"
                    onClick={() => handleToggleIncludedServices()}
                    onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                      if (e.key === 'Enter') {
                        handleToggleIncludedServices()
                      }
                    }}
                    tabIndex={0}
                    aria-label="Toggle itemized included service charges."
                  >
                    {showItemizedIncludedServices ? 'hide' : 'show'}
                  </a>
                </td>
                <td className="price">
                  {formatCurrency(totalIncludedServices)}
                </td>
              </tr>

              {showItemizedIncludedServices &&
                itemizedIncludedServices.map(({name, date, amount}) => {
                  return (
                    <tr key={`${name}-${date}`} className="text-xs">
                      <td>
                        {name} - {formatDisplayDate(toPropertyDateTime(date))}
                      </td>
                      <td className="price">{formatCurrency(amount)}</td>
                    </tr>
                  )
                })}
            </>
          )}

          {/* additional services */}
          {totalAdditionalServices > 0 && (
            <>
              <tr className="font-medium">
                <td>
                  <span className="mr-2">Total Add-on Services</span>
                  <a
                    className="text-xs link text-accent no-underline hover:underline"
                    onClick={() => handleToggleAdditionalServices()}
                    onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                      if (e.key === 'Enter') {
                        handleToggleAdditionalServices()
                      }
                    }}
                    tabIndex={0}
                    aria-label="Toggle itemized add-on service charges."
                  >
                    {showItemizedAdditionalServices ? 'hide' : 'show'}
                  </a>
                </td>
                <td className="price">
                  {formatCurrency(totalAdditionalServices)}
                </td>
              </tr>

              {showItemizedAdditionalServices &&
                itemizedAdditionalServices.map(({name, date, amount}) => {
                  return (
                    <tr key={`${name}-${date}`} className="text-xs">
                      <td>
                        {name} - {formatDisplayDate(toPropertyDateTime(date))}
                      </td>
                      <td className="price">{formatCurrency(amount)}</td>
                    </tr>
                  )
                })}
            </>
          )}

          {/* taxes and fees */}
          {totalTaxesAndFees > 0 && (
            <>
              <tr className="font-medium">
                <td>
                  <span className="mr-2">Total Taxes and Fees</span>
                  <a
                    className="text-xs link text-accent no-underline hover:underline"
                    onClick={() =>
                      setShowItemizedTaxesAndFees(!showItemizedTaxesAndFees)
                    }
                    onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                      if (e.key === 'Enter') {
                        setShowItemizedTaxesAndFees(!showItemizedTaxesAndFees)
                      }
                    }}
                    tabIndex={0}
                    aria-label="Toggle itemized taxes and fees."
                  >
                    {showItemizedTaxesAndFees ? 'hide' : 'show'}
                  </a>
                </td>
                <td className="price">{formatCurrency(totalTaxesAndFees)}</td>
              </tr>

              {showItemizedTaxesAndFees &&
                itemizedTaxesAndFees.map(({name, amount}, index) => {
                  return (
                    <tr key={`${name}-${index}`} className="text-xs">
                      <td>{name}</td>
                      <td className="price">{formatCurrency(amount)}</td>
                    </tr>
                  )
                })}
            </>
          )}

          {/* total amount */}
          <tr className="border-t">
            <td>
              <span className="font-bold mr-1">Room Total</span>
              <span className="text-xs">
                {isPrepaid ? '(Due Now)' : '(Due Later)'}
              </span>
            </td>
            <td className="font-bold price">{formatCurrency(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export default ShoppingCartPricingSummary
